import axios from 'axios';

import {
    AddRatingRequestDto,
    CardResponseDto,
    CardViewRequestDto,
    ChatRequestDto,
    ClosedDealPaymentsResponse,
    CommentRequestDto,
    CommentResponseDto,
    CommonProfileDto,
    CreateAbuseReportRequest,
    CustomPrefilledResponseDto,
    DealDisputeRequestDto,
    DealResponseDto,
    DealsProposalsRequestDto,
    DetailedAbuseReportResponseDto,
    DocumentUploadRequestDto,
    DocumentUploadResponseDto,
    FighterFilterParams,
    FileMetadata,
    FileMetadataDto,
    LoginDto,
    LoginResponse,
    MarketplaceCard,
    MarketplaceCardResponseDto,
    MessageDto,
    MessageResponseDto,
    NotificationSettingRequestDto,
    OpportunityCardRequestDto,
    Page,
    PostLikeResponseDto,
    PostRequestDto,
    PostResponseDto,
    Profile,
    ProfileViewRequestDto,
    RatingResponseDto,
    RegionResponse,
    TrackingResponseDto,
    UpdateDealsProposalsRequestDto,
    UpdateOpportunityCardDto,
    UpdateUserDto,
    UpdateUserStatus
} from '../types/types';
import { isValidUrl } from '../utils/utilMethods';

const BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8010/api/v1';
export const WEBSOCKET_URL = process.env.REACT_APP_WEBSOCKET_URL || "http://localhost:8006/ws";
export const CHAT_WEBSOCKET_URL = process.env.REACT_APP_CHAT_WEBSOCKET_URL || "http://localhost:8007/ws";
export const PROFILE_COUNT_WEBSOCKET_URL = process.env.REACT_APP_PROFILE_COUNT_WEBSOCKET_URL || "http://localhost:8005/ws";
export const DOMAIN_URL = process.env.REACT_APP_DOMAIN_URL || "http://localhost:3000";


axios.interceptors.response.use(
    response => response,
    async error => {
        if (error.response && error.response.status === 401) {
            try {
                await logoutUser();
                localStorage.clear();
                window.location.href = '/login';
            } catch (logoutError) {
                window.location.href = '/login';
            }
        }
        return Promise.reject(error);
    }
);

export const getAuthToken = (): string | null => {
    const userJson = localStorage.getItem('loginResponse');
    const user = userJson ? JSON.parse(userJson) : null;
    return user?.token || null;
};

const getCommonHeaders = (includeAuth: boolean = false): Record<string, string> => {
    const headers: Record<string, string> = {
        'Accept-Language': localStorage.getItem('i18nextLng') || 'en',
    };
    if (includeAuth) {
        const token = getAuthToken();
        if (token) {
            headers['Authorization'] = `Bearer ${token}`;
        }
    }
    return headers;
};

export const fetchCountries = async (): Promise<string[]> => {
    try {
        const response = await axios.get<string[]>(`${BASE_URL}/user/countries`, {
            headers: getCommonHeaders(),
        });
        return response.data;
    } catch (error) {
        throw error;
    }
};

export const fetchCitiesByCountry = async (country: string): Promise<string[]> => {
    try {
        const response = await axios.get<string[]>(
            `${BASE_URL}/user/countries/cities`,
            {
                params: { country },
                headers: getCommonHeaders(),
            }
        );
        return response.data;
    } catch (error) {
        throw error;
    }
};

export const fetchRegionByCountry = async (country: string): Promise<RegionResponse> => {
    try {
        const response = await axios.get<RegionResponse>(`${BASE_URL}/user/countries/region`, {
            params: { country },
            headers: getCommonHeaders(),
        });
        return response.data;
    } catch (error) {
        throw error;
    }
};

export const loginWithNormalCredentials = async (
    loginData: LoginDto
): Promise<LoginResponse> => {
    try {
        const response = await axios.post<LoginResponse>(
            `${BASE_URL}/user/normal-login`,
            loginData,
            {
                withCredentials: true,
                headers: getCommonHeaders(),
            }
        );
        return response.data;
    } catch (error) {
        throw error;
    }
};

export const checkAuthentication = async (): Promise<boolean> => {
    try {
        const response = await axios.get<boolean>(`${BASE_URL}/user/check-authentication`, {
            withCredentials: true,
            headers: getCommonHeaders(),
        });
        return response.data;
    } catch (error) {
        return false;
    }
};

export const handleLinkedInLogin = async (code: string): Promise<CustomPrefilledResponseDto> => {
    try {
        const response = await axios.post<CustomPrefilledResponseDto>(
            `${BASE_URL}/user/linkedIn?code=${encodeURIComponent(code)}`,
            {},
            {
                withCredentials: true,
                headers: getCommonHeaders(),
            }
        );
        return response.data;
    } catch (error) {
        throw error;
    }
};

export const logoutUser = async (): Promise<{ message: string }> => {
    try {
        const response = await axios.post<{ message: string }>(`${BASE_URL}/user/logout`, {}, {
            withCredentials: true,
            headers: getCommonHeaders(),
        });

        localStorage.removeItem('loginResponse');
        localStorage.removeItem('username');
        return response.data;
    } catch (error) {
        localStorage.removeItem('loginResponse');
        localStorage.removeItem('username');
        throw error;
    }
};

export const getProfile = async (): Promise<string> => {
    try {
        const response = await axios.get<string>(`${BASE_URL}/user/profile/welcome`, {
            headers: {
                ...getCommonHeaders(true),
                'Accept': '*/*',
                'ContentType': 'application/json',
            },
        });
        return response.data;
    } catch (error) {
        throw error;
    }
};

export const fetchFighters = async (params: FighterFilterParams): Promise<Profile[]> => {
    const queryParams = new URLSearchParams();

    if (params.name) queryParams.append('name', params.name);
    if (params.location) queryParams.append('location', params.location);
    if (params.trainingDisciplines && params.trainingDisciplines.length > 0) {
        params.trainingDisciplines.forEach((td: string) => queryParams.append('trainingDisciplines', td));
    }
    if (params.weightClasses && params.weightClasses.length > 0) {
        params.weightClasses.forEach((wc: string) => queryParams.append('weightClasses', wc));
    }

    if (params.titleHolder !== undefined) queryParams.append('titleHolder', String(params.titleHolder));
    if (params.wins !== undefined) queryParams.append('wins', String(params.wins));
    if (params.fights !== undefined) queryParams.append('fights', String(params.fights));

    const url = `${BASE_URL}/management/fighter?${queryParams.toString()}`;

    const response = await axios.get<Profile[]>(url, {
        headers: getCommonHeaders(true),
    });
    return response.data;
};


export const updateOpportunityCard = async (
  updatedData: UpdateOpportunityCardDto
): Promise<MarketplaceCard> => {
  try {
    const response = await axios.put<MarketplaceCard>(
      `${BASE_URL}/management/opportunity-card`,
      updatedData,
      {
        headers: getCommonHeaders(true),
      }
    );
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const updateOpportunityCardStatus = async (
    cardId: string
): Promise<any> => {
    try {
        const response = await axios.put<any>(
            `${BASE_URL}/management/opportunity-card/${cardId}`,
            {},
            {
                headers: getCommonHeaders(true),
            }
        );

        return response.data;
    } catch (error) {
        throw error;
    }
};

export const deleteOpportunityCard = async (
    cardId: string
): Promise<MessageDto> => {
    try {
        const response = await axios.delete<MessageDto>(
            `${BASE_URL}/management/opportunity-card/${cardId}`,
            {
                headers: getCommonHeaders(true),
            }
        );
        return response.data;
    } catch (error) {
        throw error;
    }
};

export const updateUser = async (
    updateUserDto: UpdateUserDto
): Promise<Profile> => {
    try {
        const response = await axios.put<Profile>(
            `${BASE_URL}/user/profile`,
            updateUserDto,
            {
                headers: {
                    ...getCommonHeaders(true),
                },
            },
        );

        return response.data;
    } catch (error) {
        throw error;
    }
};

export const createOpportunityCards = async (
  opportunityCardDto: OpportunityCardRequestDto
): Promise<MarketplaceCard> => {
  const response = await axios.post<MarketplaceCard>(
    `${BASE_URL}/management/opportunity-card`,
    opportunityCardDto,
    {
      headers: getCommonHeaders(true),
    }
  );
  return response.data;
};


export const fetchPurseOptions = async (): Promise<string[]> => {
    try {
        const response = await axios.get<string[]>(
            `${BASE_URL}/management/constants/purses`,
            {
                headers: getCommonHeaders(true),
            }
        );
        return response.data;
    } catch (error) {
        return [];
    }
};

export const fetchCurrencyOptions = async (): Promise<string[]> => {
    try {
        const response = await axios.get<string[]>(
            `${BASE_URL}/management/constants/currencies`,
            {
                headers: getCommonHeaders(true),
            }
        );
        return response.data;
    } catch (error) {
        return [];
    }
};

export const fetchWeightClasses = async (): Promise<string[]> => {
    try {
        const response = await axios.get<string[]>(
            `${BASE_URL}/management/constants/weight-classes`,
            {
                headers: getCommonHeaders(true),
            }
        );
        return response.data;
    } catch (error) {
        return [];
    }
};

export const fetchRosterStatus = async (): Promise<string[]> => {
    try {
        const response = await axios.get<string[]>(
            `${BASE_URL}/management/constants/roster-statuses`,
            {
                headers: getCommonHeaders(true),
            }
        );
        return response.data;
    } catch (error) {
        return [];
    }
};

export const fetchTrainingDisciplines = async (): Promise<string[]> => {
    try {
        const response = await axios.get<string[]>(
            `${BASE_URL}/management/constants/training-disciplines`,
            {
                headers: getCommonHeaders(true),
            }
        );
        return response.data;
    } catch (error) {
        return [];
    }
};

export const getManagers = async (params?: {
    name?: string;
    location?: string;
    type?: string;
    status?: string;
    page?: number;
    pageSize?: number;
}) => {
    try {
        const response = await axios.get(`${BASE_URL}/management/manager`, {
            params: {
                ...params,
                page: params?.page || 0,
                size: params?.pageSize || 10,
            },
            headers: getCommonHeaders(true),
        });

        return {
            data: Array.isArray(response.data.content) ? response.data.content : [],
            total: typeof response.data.totalElements === 'number' ? response.data.totalElements : 0,
            page: typeof response.data.number === 'number' ? response.data.number : 0,
            pageSize: typeof response.data.size === 'number' ? response.data.size : 10,
            totalPages: typeof response.data.totalPages === 'number' ? response.data.totalPages : 1,
        };
    } catch (error) {
        return {
            data: [],
            total: 0,
            page: 0,
            pageSize: 10,
            totalPages: 1,
        };
    }
};

export const getFighters = async (params?: {
    name?: string;
    location?: string;
    trainingDisciplines?: string;
    weightClasses?: string;
    titleHolder?: boolean;
    wins?: string;
    fights?: number;
    status?: string;
    gender?: string;
    page?: number;
    pageSize?: number;
}) => {
    try {
        const response = await axios.get(`${BASE_URL}/management/fighter`, {
            params: {
                ...params,
                page: params?.page || 0,
                size: params?.pageSize || 2,
            },
            headers: getCommonHeaders(true),
        });

        console.log('Raw API Response:', response.data);

        return {
            data: Array.isArray(response.data.content) ? response.data.content : [],
            total: typeof response.data.totalElements === 'number' ? response.data.totalElements : 0,
            page: typeof response.data.number === 'number' ? response.data.number : 0,
            pageSize: typeof response.data.size === 'number' ? response.data.size : 2,
            totalPages: typeof response.data.totalPages === 'number' ? response.data.totalPages : 1,
        };
    } catch (error) {
        console.error('Error fetching fighters:', error);
        return {
            data: [],
            total: 0,
            page: 0,
            pageSize: 2,
            totalPages: 1,
        };
    }
};
export const getPendingProfiles = async (params?: {
    profileType?: string;
    name?: string;
    location?: string;
    trainingDiscipline?: string;
    weightClass?: string;
    titleHolder?: boolean;
    wins?: string;
    fights?: number;
    type?: string;
    status?: string;
    page?: number;
    pageSize?: number;
}) => {
    try {
        const response = await axios.get(`${BASE_URL}/admin/profile/inactive`, {
            params: {
                ...params,
                page: params?.page || 0,
                size: params?.pageSize || 10,
            },
            headers: getCommonHeaders(true),
        });

        return {
            data: Array.isArray(response.data.content) ? response.data.content : [],
            total: typeof response.data.totalElements === "number" ? response.data.totalElements : 0,
            page: typeof response.data.number === "number" ? response.data.number : 0,
            pageSize: typeof response.data.size === "number" ? response.data.size : 10,
            totalPages: typeof response.data.totalPages === "number" ? response.data.totalPages : 1,
        };
    } catch (error) {
        console.error("Error fetching profiles:", error);
        return {
            data: [],
            total: 0,
            page: 0,
            pageSize: 10,
            totalPages: 1,
        };
    }
};

export const getReadyRosterCards = async (params?: {
    fighterId?: number;
    name?: string;
    location?: string;
    combatSport?: string;
    weightClass?: string;
    wins?: string;
    titleHolder?: boolean;
    fights?: number;
    page?: number;
    size?: number;
}) => {
    try {
        const queryParams: Record<string, any> = {
            page: 0,
            size: 10,
            ...params
        };

        if (params?.combatSport) {
            queryParams.combatSport = params.combatSport;
        }

        if (params?.weightClass) {
            queryParams.weightClass = params.weightClass;
        }

        if (params?.wins) {
            queryParams.wins = params.wins;
        }

        if (params?.titleHolder !== undefined) {
            queryParams.titleHolder = params.titleHolder;
        }

        if (params?.fights) {
            queryParams.fights = params.fights;
        }

        const response = await axios.get(`${BASE_URL}/management/ready-roster`, {
            params: queryParams,
            paramsSerializer: (params) => new URLSearchParams(params).toString(),
            headers: getCommonHeaders(true),
        });

        if (response.data && response.data.content && Array.isArray(response.data.content)) {
            return {
                content: response.data.content,
                totalElements: response.data.totalElements,
                totalPages: response.data.totalPages,
                pageNumber: response.data.number,
                pageSize: response.data.size,
                first: response.data.first,
                last: response.data.last
            };
        }

        return {
            content: [],
            totalElements: 0,
            totalPages: 0,
            pageNumber: 0,
            pageSize: 10,
            first: true,
            last: true
        };
    } catch (error) {
        console.error("Error fetching ready roster cards:", error);
        return {
            content: [],
            totalElements: 0,
            totalPages: 0,
            pageNumber: 0,
            pageSize: 10,
            first: true,
            last: true
        };
    }
};

export const createReadyRosterCard = async (data: any) => {
    try {
        const response = await axios.post(
            `${BASE_URL}/management/ready-roster`,
            data,
            {
                headers: {
                    'Content-Type': 'application/json',
                    ...getCommonHeaders(true),
                },
            }
        );

        return response.data;
    } catch (error) {
        throw error;
    }
};

export const getReadyRosterCard = async (cardId: any) => {
    try {
        const response = await axios.get(
            `${BASE_URL}/management/ready-roster/${cardId}`,
            {
                headers: {
                    'Content-Type': 'application/json',
                    ...getCommonHeaders(true),
                },
            }
        );

        return response.data;
    } catch (error: any) {
        throw error.response?.data || error;
    }
};

export const getOpportunityCard = async (cardId: any) => {
    try {
        const response = await axios.get(
            `${BASE_URL}/management/opportunity-card/${cardId}`,
            {
                headers: {
                    'Content-Type': 'application/json',
                    ...getCommonHeaders(true),
                },
            }
        );

        return response.data;
    } catch (error: any) {
        throw error.response?.data || error;
    }
};


export const updateReadyRosterCard = async (data: any) => {
    try {
        const response = await axios.put(
            `${BASE_URL}/management/ready-roster`,
            data,
            {
                headers: {
                    'Content-Type': 'application/json',
                    ...getCommonHeaders(true),
                },
            }
        );

        return response.data;
    } catch (error) {
        throw error;
    }
};

export const deleteReadyRosterCard = async (cardId: string | number) => {
    try {
        const response = await axios.delete(`${BASE_URL}/management/ready-roster/${cardId}`, {
            headers: {
                ...getCommonHeaders(true),
            },
        });

        return response.data;
    } catch (error) {
        console.error("Error deleting ready roster card:", error);
        throw error;
    }
};

export const getAllNotifications = async () => {
    try {
        const response = await axios.get(`${BASE_URL}/notification`, {
            headers: {
                'Content-Type': 'application/json',
                ...getCommonHeaders(true),
            },
        });

        return response.data;
    } catch (error) {
        throw error;
    }
};

export const createDealProposal = async (dealData: DealsProposalsRequestDto) => {
    try {
        const response = await axios.post(`${BASE_URL}/deal`, dealData, {
            headers: {
                'Content-Type': 'application/json',
                'USERNAME': JSON.parse(localStorage.getItem("loginResponse") || "{}").username,
                ...getCommonHeaders(true),
            },
        });

        if (!response.data) {
            throw new Error('Failed to create deal proposal');
        }

        return response.data;
    } catch (error) {
        throw error;
    }
};

export const createContact = async (
    type: string,
    cardId?: number | string
): Promise<any> => {
    try {
        const params = new URLSearchParams();

        if (type === "readyroster" && cardId !== undefined) {
            params.append("marketplaceCardId", cardId.toString());
        } else if (type === "opportunity" && cardId !== undefined) {
            params.append("marketplaceCardId", cardId.toString());
        }

        const query = params.toString();

        const fullUrl = `${BASE_URL}/management/contacts${query ? "?" + query : ""}`;

        const response = await axios.post<any>(
            fullUrl,
            {},
            {
                headers: {
                    ...getCommonHeaders(true),
                },
            }
        );

        return response.data;
    } catch (error) {
        throw error;
    }
};

export const updateReadyRosterCardStatus = async (
    cardId: string | number
): Promise<any> => {
    try {
        const response = await axios.put<any>(
            `${BASE_URL}/management/ready-roster/${cardId}`,
            {},
            {
                headers: getCommonHeaders(true),
            }
        );
        return response.data;
    } catch (error) {
        throw error;
    }
};

export const updateNotificationViewStatus = async (
    notificationId: number
): Promise<void> => {
    try {
        await axios.put(
            `${BASE_URL}/notification/status/${notificationId}`,
            {},
            {
                headers: {
                    'Content-Type': 'application/json',
                    ...getCommonHeaders(true),
                },
            }
        );
    } catch (error) {
        throw error;
    }
};

export const uploadMediaFile = async (
    file: File,
    fieldName: string = "file"
): Promise<string> => {
    const formData = new FormData();
    formData.append(fieldName, file);

    try {
        const response = await axios.post(`${BASE_URL}/user/upload-file`, formData, {
            headers: {
                "Content-Type": "multipart/form-data",
                ...getCommonHeaders(true),
            },
        });

        if (typeof response.data === "string" && isValidUrl(response.data)) {
            return response.data;
        } else if (response.data && typeof response.data === "object" && response.data.url) {
            return response.data.url;
        } else {
            return response.data;
        }
    } catch (error) {
        throw error;
    }
};

export const fetchProfile = async (): Promise<Profile | null> => {
    try {
        const response = await axios.get<Profile>(
            `${BASE_URL}/user/profile`,
            {
                headers: {
                    ...getCommonHeaders(true),
                    USERNAME: localStorage.getItem("username") || "",
                },
            }
        );
        return response.data;
    } catch (error) {
        return null;
    }
};

export const updateUserStatus = async (payload: UpdateUserStatus): Promise<any> => {
    try {
        const response = await axios.put(
            `${BASE_URL}/admin/profile/status`,
            payload,
            {
                headers: {
                    ...getCommonHeaders(true),
                    USERNAME: localStorage.getItem("username") || "",
                },
            }
        );
        return response.data;
    } catch (error) {
        throw error;
    }
};

export interface FetchActivityFilters {
    title?: string;
    startDate?: string;
    endDate?: string;
    description?: string;
    page?: number;
    size?: number;
}

export const fetchActivities = async (filters: FetchActivityFilters = {}): Promise<any[]> => {
    try {
        const queryParams = new URLSearchParams();
        if (filters.title) queryParams.append('title', filters.title);
        if (filters.startDate) queryParams.append('startDate', filters.startDate);
        if (filters.endDate) queryParams.append('endDate', filters.endDate);
        if (filters.description) queryParams.append('description', filters.description);
        if (filters.page !== undefined) queryParams.append('page', String(filters.page));
        if (filters.size !== undefined) queryParams.append('size', String(filters.size));

        const response = await axios.get(`${BASE_URL}/management/activities?${queryParams.toString()}`, {
            headers: getCommonHeaders(true),
        });

        return response.data?.content || response.data || [];
    } catch (error) {
        return [];
    }
};

export const fetchActivityTitles = async (): Promise<string[]> => {
    try {
        const response = await axios.get(`${BASE_URL}/management/constants/activity-titles`, {
            headers: getCommonHeaders(true),
        });
        return response.data || [];
    } catch (error) {
        return [];
    }
};

export const updateDealDetails = async (
    dto: UpdateDealsProposalsRequestDto
): Promise<DealResponseDto> => {
    try {
        const response = await axios.put<DealResponseDto>(
            `${BASE_URL}/deal`,
            dto,
            {
                headers: {
                    ...getCommonHeaders(true),
                    'USERNAME': JSON.parse(localStorage.getItem("loginResponse") || "{}").username,
                },
            }
        );
        return response.data;
    } catch (error) {
        throw error;
    }
};

export const getAllMessages = async (
    receiverId: string | number,
    type: string
): Promise<MessageResponseDto[]> => {
    try {
        const response = await axios.get<MessageResponseDto[]>(
            `${BASE_URL}/chat/all/${receiverId}/${type}`,
            {
                headers: {
                    ...getCommonHeaders(true),
                },
            }
        );
        return response.data;
    } catch (error) {
        throw error;
    }
};

export const sendMessage = async (
    payload: ChatRequestDto,
): Promise<any> => {
    try {
        const response = await axios.post(
            `${BASE_URL}/chat`,
            payload,
            {
                headers: {
                    ...getCommonHeaders(true),
                },
            }
        );
        return response.data;
    } catch (error) {
        throw error;
    }
};

export const getProfileById = async (profileId: string | number) => {
    try {

        const response = await axios.get(`${BASE_URL}/user/public`, {
            params: { profileId },

        });

        return {
            success: true,
            data: response.data as Profile,
        };
    } catch (error) {
        return {
            success: false,
            data: null,
        };
    }
};




export const fetchSocialMediaPlatforms = async (): Promise<string[]> => {
    try {
        const response = await axios.get<string[]>(
            `${BASE_URL}/user/enum/social-media-platforms`,
            {
                headers: getCommonHeaders(true),
            }
        );
        return response.data;
    } catch (error) {
        return [];
    }
};

export const changeLanguage = async (language: string): Promise<string> => {
    try {
        const response = await axios.post<string>(
            `${BASE_URL}/user/profile/language?language=${encodeURIComponent(language)}`,
            null,
            {
                headers: {
                    ...getCommonHeaders(true),
                },
            }
        );

        return response.data;
    } catch (error) {
        throw error;
    }
};

export const getPendingProfilesCount = async (): Promise<number> => {
    try {
        const response = await axios.get<number>(`${BASE_URL}/management/profiles/count`, {
            headers: getCommonHeaders(true),
        });

        return response.data;
    } catch (error) {
        console.error("Error fetching pending profiles count:", error);
        return 0;
    }
};


export const uploadMultipleFile = async (
    files: File[],
    fieldName: string = "file"
): Promise<FileMetadata[]> => {
    const formData = new FormData();

    files.forEach((file) => {
        formData.append(fieldName, file);
    });

    try {
        const response = await axios.post(`${BASE_URL}/user/upload-chat-file`, formData, {
            headers: {
                "Content-Type": "multipart/form-data",
                ...getCommonHeaders(true),
            },
        });


        if (Array.isArray(response.data)) {
            return response.data as FileMetadata[];
        } else {
            throw new Error("Invalid response format from upload API");
        }
    } catch (error) {
        throw error;
    }
};


export const getReportsByUser = async (username: string): Promise<DetailedAbuseReportResponseDto[]> => {
    try {
        const response = await axios.get<DetailedAbuseReportResponseDto[]>(
            `${BASE_URL}/admin/reports`,
            {
                headers: {
                    ...getCommonHeaders(true),
                    USERNAME: username,
                },
            }
        );
        return response.data;
    } catch (error) {
        throw error;
    }
};




export const createAbuseReport = async (
    payload: CreateAbuseReportRequest
): Promise<string> => {
    try {
        const response = await axios.post<string>(
            `${BASE_URL}/management/reports`,
            payload,
            {
                headers: {
                    ...getCommonHeaders(true),
                },
            }
        );
        return response.data;
    } catch (error) {
        throw error;
    }
};


export const getAllChatNotifications = async () => {
    try {
        const response = await axios.get(`${BASE_URL}/notification/chat`, {
            headers: {
                'Content-Type': 'application/json',
                ...getCommonHeaders(true),
            },
        });

        return response.data;
    } catch (error) {
        throw error;
    }
};


export const verifyProfile = async (
    profileId: string | number,
    isVerified: boolean,
    username: string
): Promise<Profile> => {
    try {
        const response = await axios.put<Profile>(
            `${BASE_URL}/admin/profile/verify`,
            null,
            {
                headers: {
                    ...getCommonHeaders(true),
                    USERNAME: username,
                },
                params: {
                    profileId,
                    isVerified,
                },
            }
        );
        return response.data;
    } catch (error) {
        throw error;
    }
};



export const createProfileView = async (
    dto: ProfileViewRequestDto
): Promise<string> => {
    try {
        const response = await axios.post<string>(
            `${BASE_URL}/tracking/public/profile-view`,
            dto,
            {
                headers: {
                    ...getCommonHeaders(true),
                },
            }
        );
        return response.data;
    } catch (error) {
        throw error;
    }
};


export const createPublicProfileView = async (
    dto: ProfileViewRequestDto
): Promise<string> => {
    try {
        const response = await axios.post<string>(
            `${BASE_URL}/tracking/public/profile-view`,
            dto,
            {
                headers: {
                    ...getCommonHeaders(true),
                },
            }
        );
        return response.data;
    } catch (error) {
        throw error;
    }
};

export const createCardView = async (
    username: string,
    dto: CardViewRequestDto
): Promise<string> => {
    try {
        const response = await axios.post<string>(
            `${BASE_URL}/tracking/card-view`,
            dto,
            {
                headers: {
                    ...getCommonHeaders(true),
                    USERNAME: username,
                },
            }
        );
        return response.data;
    } catch (error) {
        throw error;
    }
};


export const getProfileViewStats = async (
    username: string,
    range?: '7days' | 'month' | 'all'
): Promise<TrackingResponseDto> => {
    try {
        const response = await axios.get<TrackingResponseDto>(
            `${BASE_URL}/tracking/profile-view-stats`,
            {
                headers: {
                    ...getCommonHeaders(true),
                    USERNAME: username,
                },
                params: range === '7days' || !range ? {} : { range },
            }
        );
        return response.data;
    } catch (error) {
        throw error;
    }
};



export const getCardViewStats = async (
    username: string,
    range: string = 'all',
    dateSort?: string,
    viewsSort?: string
): Promise<CardResponseDto> => {
    try {
        const response = await axios.get<CardResponseDto>(
            `${BASE_URL}/tracking/card-view-stats`,
            {
                headers: {
                    ...getCommonHeaders(true),
                    USERNAME: username,
                },
                params: {
                    range,
                    ...(dateSort && { dateSort }),
                    ...(viewsSort && { viewsSort }),
                },
            }
        );
        return response.data;
    } catch (error) {
        throw error;
    }
};


export const getCardViewStatsById = async (
    username: string,
    cardId: string,
    type: string,
    range?: string
): Promise<TrackingResponseDto> => {
    try {
        const params: Record<string, string> = {
            cardId,
            type: type.toUpperCase(),
        };

        if (range && range.toLowerCase() !== '7days') {
            params.range = range.toLowerCase();
        }

        const response = await axios.get<TrackingResponseDto>(
            `${BASE_URL}/tracking/card`,
            {
                headers: {
                    ...getCommonHeaders(true),
                    USERNAME: username,
                },
                params,
            }
        );

        return response.data;
    } catch (error) {
        throw error;
    }
};



export const updateNotificationSetting = async (
    username: string,
    dto: NotificationSettingRequestDto
): Promise<string> => {
    try {
        const response = await axios.post<string>(
            `${BASE_URL}/user/profile/setting/notification`,
            dto,
            {
                headers: {
                    ...getCommonHeaders(true),
                    USERNAME: username,
                },
            }
        );
        return response.data;
    } catch (error) {
        throw error;
    }
};

export const getProfileForSetting = async (username: string): Promise<Profile> => {
    try {
        const response = await axios.get<Profile>(
            `${BASE_URL}/user/profile`,
            {
                headers: {
                    ...getCommonHeaders(true),
                    USERNAME: username,
                },
            }
        );
        return response.data;
    } catch (error) {
        throw error;
    }
};

export const getProfileUser = async (profileId: string | number, username: string): Promise<Profile> => {
    try {
        const response = await axios.get<Profile>(
            `${BASE_URL}/user`,
            {
                headers: {
                    ...getCommonHeaders(true),
                    USERNAME: username,
                },
                params: {
                    profileId,
                },
            }
        );
        return response.data;
    } catch (error) {
        throw error;
    }
}

export const getAllSubregions = async (): Promise<string[]> => {
    try {
        const response = await axios.get<string[]>(`${BASE_URL}/user/countries/subregions`, {
            headers: {
                ...getCommonHeaders(true),
            }
        });
        return response.data;
    } catch (error) {
        throw error;
    }
}

export const getConfirmedDeals = async (
    status?: string,
    fromDate?: string,
    toDate?: string,
    page: number = 0,
    size: number = 10
): Promise<DealResponseDto[]> => {

    try {
        const response = await axios.get<DealResponseDto[]>(
            `${BASE_URL}/deal`,
            {
                headers: {
                    'Content-Type': 'application/json',
                    'USERNAME': JSON.parse(localStorage.getItem("loginResponse") || "{}").username,
                    ...getCommonHeaders(true),
                },

                params: {
                    status,
                    fromDate,
                    toDate,
                    page,
                    size
                }
            }
        );

        return response.data;

    } catch (error) {
        throw error;
    }
};

export const updateActivationRequired = async (
    username: string
): Promise<string> => {
    try {
        const response = await axios.put<string>(
            `${BASE_URL}/user/profile/setting/activation`,
            null,
            {
                headers: {
                    ...getCommonHeaders(true),
                    USERNAME: username,
                },
            }
        );
        return response.data;
    } catch (error) {
        throw error;
    }
};

export const getAllPosts = async (
  username: string,
  isMyPost: boolean = false,
  filters?: {
    title?: string;
    trainingDiscipline?: string;
    weightClass?: string;
    managerType?: string;
    profileType?: string;
  }
): Promise<{ posts: PostResponseDto[]; profiles: CommonProfileDto[] }> => {
  try {
    const params: any = { isMyPost };
    if (filters) {
      Object.keys(filters).forEach((key) => {
        if (filters[key as keyof typeof filters] !== undefined) {
          params[key] = filters[key as keyof typeof filters];
        }
      });
    }

    const response = await axios.get<{ posts: PostResponseDto[]; profiles: CommonProfileDto[] }>(
      `${BASE_URL}/socialmedia/post`,
      {
        headers: {
          ...getCommonHeaders(true),
          USERNAME: username,
        },
        params,
      }
    );

    return response.data;
  } catch (error) {
    throw error;
  }
};



export const updatePost = async (
    username: string,
    postId: number,
    dto: PostRequestDto
): Promise<PostResponseDto> => {
    const response = await axios.put<PostResponseDto>(
        `${BASE_URL}/socialmedia/post/${postId}`,
        dto,
        {
            headers: {
                ...getCommonHeaders(true),
                USERNAME: username,
            },
        }
    );
    return response.data;
};
export const deletePost = async (
    username: string,
    postId: number
): Promise<MessageDto> => {
    const response = await axios.delete<MessageDto>(`${BASE_URL}/socialmedia/post/${postId}`, {
        headers: {
            ...getCommonHeaders(true),
            USERNAME: username,
        },
    });
    return response.data;
};

export const createComment = async (
    username: string,
    dto: CommentRequestDto
): Promise<CommentResponseDto> => {
    const response = await axios.post<CommentResponseDto>(
        `${BASE_URL}/socialmedia/post/comment`,
        dto,
        {
            headers: {
                ...getCommonHeaders(true),
                USERNAME: username,
            },
        }
    );
    return response.data;
};

export const deleteComment = async (
    username: string,
    commentId: number
): Promise<MessageDto> => {
    const response = await axios.delete<MessageDto>(
        `${BASE_URL}/socialmedia/post/comment/${commentId}`,
        {
            headers: {
                ...getCommonHeaders(true),
                USERNAME: username,
            },
        }
    );
    return response.data;
};

export const getCommentsByPost = async (
  username: string,
  postId: number
): Promise<CommentResponseDto[]> => {
  try {
    const response = await axios.get<CommentResponseDto[] | Page<CommentResponseDto>>(
      `${BASE_URL}/socialmedia/post/comments`,
      {
        params: {
          postId,
        },
        headers: {
          ...getCommonHeaders(true),
          USERNAME: username,
        },
      }
    );

    if (Array.isArray(response.data)) {
      return response.data;
    }

    return response.data?.content || [];
  } catch (error) {
    if (axios.isAxiosError(error)) {
      throw new Error(
        error.response?.data?.message ||
          error.message ||
          "Failed to fetch comments"
      );
    }

    throw new Error("Failed to fetch comments");
  }
};


export const toggleLike = async (
    username: string,
    postId: number,
    isLike: boolean
): Promise<MessageDto> => {
    const response = await axios.post<MessageDto>(
        `${BASE_URL}/socialmedia/post/${postId}/like`,
        null,
        {
            headers: {
                ...getCommonHeaders(true),
                USERNAME: username,
            },
            params: { postId, isLike },
        }
    );
    return response.data;
};

export const getLikesByPost = async (
    postId: number
): Promise<PostLikeResponseDto[]> => {
    const response = await axios.get<PostLikeResponseDto[]>(
        `${BASE_URL}/socialmedia/post/${postId}/likes`,

        {
            headers: {
                ...getCommonHeaders(true)
            }
        }
    );
    return response.data;
};

export const createPost = async (
    username: string,
    dto: PostRequestDto
): Promise<string> => {
    try {
        const response = await axios.post<string>(
            `${BASE_URL}/socialmedia/post`,
            dto,
            {
                headers: {
                    ...getCommonHeaders(true),
                    USERNAME: username,
                },
            }
        );
        return response.data;
    } catch (error) {
        throw error;
    }
};

export const uploadSingleFile = async (
    file: File,
    fieldName: string = "file"
): Promise<FileMetadataDto> => {
    const formData = new FormData();
    formData.append(fieldName, file);

    try {
        const response = await axios.post<FileMetadataDto>(
            `${BASE_URL}/user/upload-single-file`,
            formData,
            {
                headers: getCommonHeaders(true),
            }
        );
        return response.data;
    } catch (error: any) {
        throw error;
    }
};

export const getPost = async (
    postId: number
): Promise<PostResponseDto> => {
    try {
        const response = await axios.get<PostResponseDto>(
            `${BASE_URL}/socialmedia/post/${postId}`,
            {
                headers: {
                    ...getCommonHeaders(true),
                },
            }
        );
        return response.data;
    } catch (error) {
        throw error;
    }
};




export const getAllRatings = async (
    profileId: string | number,
    page: number = 0,
    size: number = 10
): Promise<Page<RatingResponseDto>> => {
    try {
        const response = await axios.get<Page<RatingResponseDto>>(
            `${BASE_URL}/rating/by-profile`,
            {
                headers: {
                    ...getCommonHeaders(true),
                },
                params: { page, size, profileId },
            }
        );

        return response.data;
    } catch (error) {
        throw error;
    }
};


export const submitRatingOnly = async (
  ratingData: AddRatingRequestDto
): Promise<any> => {
  const res = await axios.post(`${BASE_URL}/rating`, ratingData, {
    headers: getCommonHeaders(true),
  });
  return res.data;
};

 
export const updateFightResultOnly = async (
  dealId: string | number,
  fightResult: string | null,
  fighterAccepted?: boolean,
  dispute?: DealDisputeRequestDto 
): Promise<any> => {
  const payload: any = {
    dealId,
    fightResult,
  };

  if (fighterAccepted !== undefined) {
    payload.fighterAccepted = fighterAccepted;

    if (fighterAccepted === false && dispute) {
      payload.dispute = dispute;
    }
  }

  const res = await axios.put(`${BASE_URL}/deal/updateResult`, payload, {
    headers: getCommonHeaders(true),
  });
  return res.data;
};

const wait = (milliseconds: number) =>
new Promise((resolve) => setTimeout(resolve, milliseconds));


export const verifyPayment = async (sessionId: string): Promise<any> => {
try {


 const token = await waitUntilTokenAvailable();


if (!token) {
 console.error("Token not available after waiting");
 return null;
 }
const response = await axios.get(
 `${BASE_URL}/payment/verify`,
 {
 headers: {
 Authorization: `Bearer ${token}`,
},
 params: { sessionId },
 }
 );


 return response.data;
} catch (error) {
 throw error;
}

};

export const getDealById = async (
  dealId: string | number
): Promise<DealResponseDto> => {
  try {
    const response = await axios.get<DealResponseDto>(
      `${BASE_URL}/deal/${dealId}`,
      {
        headers: {
          ...getCommonHeaders(true),
        },
      }
    );

    return response.data;
  } catch (error: any) {
    throw error.response?.data?.message || error.message || "Something went wrong";
  }
};

export const getClosedDealPayments = async (): Promise<ClosedDealPaymentsResponse> => {
    try {
        const response = await axios.get<ClosedDealPaymentsResponse>(
            `${BASE_URL}/payment`,
            {
                headers: {
                    ...getCommonHeaders(true),
                },
            }
        );

        return response.data;
    } catch (error: any) {
        throw (
            error.response?.data?.message ||
            error.message ||
            "Failed to fetch closed deal payments"
        );
    }
};

export const downloadDealOverviewPdf = async (dealId: string | number): Promise<Blob> => {
  const loginResponse = JSON.parse(localStorage.getItem("loginResponse") || "{}");
  if (!loginResponse?.username && !loginResponse?.email) {
    throw new Error("User not authenticated");
  }

  const username = loginResponse.username || loginResponse.email?.split("@")[0];

  const response = await axios.get(`${BASE_URL}/deal/${dealId}/overview-pdf`, {
    headers: {
      ...getCommonHeaders(true),
      username: username,
    },
    responseType: "blob",
  });

  return response.data; 
};

export const downloadDealPaymentReceiptPdf = async (dealId: string | number): Promise<Blob> => {
  const loginResponse = JSON.parse(localStorage.getItem("loginResponse") || "{}");
  if (!loginResponse?.username && !loginResponse?.email) {
    throw new Error("User not authenticated");
  }
 
  const username = loginResponse.username || loginResponse.email?.split("@")[0];
 
  const response = await axios.get(
    `${BASE_URL}/payment/${dealId}/payment-pdf`,
    {
      headers: {
        ...getCommonHeaders(true),
        username: username,
      },
      responseType: "blob",
    }
  );
 
  return response.data;
};

export const formatPurseText = (purse: any): string => {
  if (purse == null) {
    return ''; 
  }
  if (typeof purse === 'string' && purse.toLowerCase() === 'negotiable') {
    return 'Negotiable';
  } else {

    return Number(purse).toLocaleString('en-US');
  }
};


export const getActiveDeals = async () => {
    try {
        const response = await axios.get(`${BASE_URL}/deal/active`, {
            headers: {
                'Content-Type': 'application/json',
                'USERNAME': JSON.parse(localStorage.getItem("loginResponse") || "{}").username,
                ...getCommonHeaders(true),
            },
        });

        if (!response.data) {
            throw new Error('Failed to fetch active deals');
        }

        return response.data;
    } catch (error) {
        throw error;
    }
};

export const getPostsByProfileId = async (
  profileId: string | number,
  page: number = 0,
  size: number = 10
): Promise<any> => {
  try {
    const response = await axios.get(`${BASE_URL}/socialmedia/post/profile`, {
      params: {
        profileId,
        page,
        size,
      },
      headers: {
        ...getCommonHeaders(true),
      },
    });

    return response.data;
  } catch (error: any) {
    throw (
      error.response?.data?.message ||
      error.message ||
      "Failed to fetch posts"
    );
  }
};

export const getOptionsBySource = async (optionsSource: string): Promise<string[]> => {
  try {
    const response = await axios.get(
      `${BASE_URL}${optionsSource}`,
      {
        headers: {
          ...getCommonHeaders(true),
        },
      }
    );

    return response.data;
  } catch (error: any) {
    throw (
      error.response?.data?.message ||
      error.message ||
      `Failed to fetch options from ${optionsSource}`
    );
  }
};


export const uploadRequiredDocuments = async (
  request: DocumentUploadRequestDto
): Promise<DocumentUploadResponseDto> => {
  try {
    const response = await axios.post<DocumentUploadResponseDto>(
      `${BASE_URL}/deal/documents/upload`,
      request,
      {
        headers: {
          ...getCommonHeaders(true),
        },
      }
    );

    return response.data;
  } catch (error: any) {
    console.error("Upload Documents Error:", error);
    throw error.response?.data?.message || error.message || "Something went wrong";
  }
};

export const createCheckoutSession = async (
    username: string,
    dealId: string | number,
    componentPaymentId: string | number
): Promise<{ url?: string; checkoutUrl?: string }> => {
    try {
        const response = await axios.post(
            `${BASE_URL}/stripe/create-checkout-session`,
            null,
            {
                params: { username, dealId, componentPaymentId },
                headers: getCommonHeaders(true),
            }
        );
        return response.data;
    } catch (error) {
        throw error;
    }
};

export const waitUntilTokenAvailable = async (maxWaitTimeMs = 5000, intervalMs = 200): Promise<string | null> => {
    const startTime = Date.now();
    while (Date.now() - startTime < maxWaitTimeMs) {
        const token = getAuthToken();
        if (token) return token;
        await new Promise(resolve => setTimeout(resolve, intervalMs));
    }
    return getAuthToken();
};

export const getAllMarketplaceCards = async (
    username: string,
    filters?: {
        name?: string;
        location?: string;
        sport?: string;
        weightClass?: string;
        purse?: string;
        titleShot?: boolean;
        myCard?: boolean;
        opportunityType?: string;
        rosterStatus?: string;
        role?: string;
        isPublished?: boolean;
        fighterId?: string;
        page?: number;
        size?: number;
    }
): Promise<Page<MarketplaceCardResponseDto>> => {
    try {
        const params: any = {};
        if (filters) {
            Object.keys(filters).forEach((key) => {
                const value = filters[key as keyof typeof filters];
                if (value !== undefined && value !== null) {
                    params[key] = value;
                }
            });
        }

        const response = await axios.get<Page<MarketplaceCardResponseDto>>(
            `${BASE_URL}/management/marketplace/marketplace-cards`,
            {
                headers: {
                    ...getCommonHeaders(true),
                    USERNAME: username,
                },
                params,
            }
        );

        return response.data;
    } catch (error) {
        throw error;
    }
};

export const fetchOpportunityTypes = async (): Promise<string[]> => {
    try {
        const response = await axios.get(
            `${BASE_URL}/management/constants/opportunity-types`,
            {
                headers: getCommonHeaders(true),
            }
        );

        return response.data || [];
    } catch (error) {
        return [];
    }
};